import { instance } from '../api/api';
import { IDriverUser, IUserBase } from '../types/type';
import { toApiError } from '../utils/apiError';

export interface ProfilesResponse {
  data: IUserBase[];
  page: number;
  totalCount: number;
  totalPages: number;
}

export interface ProfilesFilters {
  page?: number;
  limit?: number;
  role?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  isActive?: boolean;
  // true — только водители, завершившие регистрацию
  registered?: boolean;
}

// Имя сотрудника: GET /profiles/:id/name доступен любой роли персонала. Пустое имя — "".
// displayName — "Имя Фамилия", без имени — логин сотрудника; у водителя без имени — "" (логин — телефон).
export interface IProfileName {
  id: string;
  firstName: string;
  lastName: string;
  displayName: string;
}

// Поля записи водителя; у водителя, не завершившего регистрацию, их нет
type DriverRecordFields = Pick<
  IDriverUser,
  | 'snils'
  | 'birthDate'
  | 'sex'
  | 'licenseSer'
  | 'licenseNumber'
  | 'licenseIssueDate'
  | 'licenseExpireDate'
  | 'cars'
  | 'roadlists'
  | 'medlists'
  | 'techlists'
>;

// GET /profiles/driver/:id: запись водителя встроена в ответ, её поля на верхнем уровне рядом с profile.
// У незарегистрированного водителя id = null и полей записи нет, profile есть всегда.
export interface IDriverDetailsResponse extends Partial<DriverRecordFields> {
  registered: boolean;
  id: string | null;
  profile: IUserBase;
}

export interface IDriverDetails {
  // Профиль вместе с полями записи водителя; driverID — id записи, '' если её нет
  user: IUserBase & Partial<DriverRecordFields>;
  // Регистрация в приложении завершена
  registered: boolean;
  // Запись водителя есть — вкладки водителя показываем, даже если он деактивирован
  hasDriverData: boolean;
}

export function toDriverDetails({ profile, registered, id, ...driver }: IDriverDetailsResponse): IDriverDetails {
  return {
    user: { ...driver, ...profile, driverID: id ?? '' },
    registered: registered === true,
    hasDriverData: id !== null,
  };
}

export const UserService = {
  async getProfiles(filters: ProfilesFilters = {}): Promise<ProfilesResponse> {
    try {
      const queryParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });

      const response = await instance.get(`/profiles?${queryParams.toString()}`);

      // Проверяем структуру ответа и обрабатываем разные варианты
      let data: IUserBase[] = [];
      let page = 1;
      let totalCount = 0;
      let totalPages = 1;

      if (response.data) {
        // Если ответ уже содержит массив данных
        if (Array.isArray(response.data)) {
          data = response.data;
        }
        // Если ответ содержит объект с полем data
        else if (response.data.data && Array.isArray(response.data.data)) {
          data = response.data.data;
          page = response.data.page || 1;
          totalCount = response.data.totalCount || data.length;
          totalPages = response.data.totalPages || 1;
        }
        // Если ответ имеет другую структуру, пытаемся адаптировать
        else {
          // Пытаемся извлечь массив из первого свойства объекта
          const firstKey = Object.keys(response.data)[0];
          if (firstKey && Array.isArray(response.data[firstKey])) {
            data = response.data[firstKey];
          }
        }
      }

      return { data, page, totalCount, totalPages };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить список профилей');
    }
  },

  async getTechnicians(filters: Omit<ProfilesFilters, 'role'> = {}): Promise<ProfilesResponse> {
    try {
      const result = await this.getProfiles({ ...filters, role: 'technik' });

      // Если пустой список, попробуем альтернативный запрос
      if (!result.data.length) {
        try {
          const altResponse = await instance.get('/profiles/technics');
          if (altResponse.data && Array.isArray(altResponse.data)) {
            return {
              data: altResponse.data,
              page: 1,
              totalCount: altResponse.data.length,
              totalPages: 1,
            };
          }
        } catch (altError) {
          // Ошибку игнорируем, продолжаем с тем, что есть
        }
      }

      return result;
    } catch (error) {
      // Возвращаем пустой результат вместо выбрасывания исключения
      return { data: [], page: 1, totalCount: 0, totalPages: 1 };
    }
  },

  // id — id профиля водителя
  async getDriverDetails(id: string): Promise<IDriverDetails> {
    try {
      const { data } = await instance.get<IDriverDetailsResponse>(`/profiles/driver/${id}`);
      return toDriverDetails(data);
    } catch (error) {
      throw toApiError(error, 'Не удалось загрузить водителя');
    }
  },

  // Только имя: полный профиль (GET /profiles/:id) сотруднику парка доступен не всегда
  async getProfileName(id: string): Promise<IProfileName> {
    try {
      const { data } = await instance.get<IProfileName>(`/profiles/${id}/name`);
      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Не удалось получить имя сотрудника');
    }
  },
};
